import { api } from '../api/client.ts';
import type { Presentation, Settings } from '../api/types.ts';

/**
 * PUT /settings replaces presentation and timeZone together, so every write goes through this one
 * queue and carries the latest known value of the field it is not changing; two settings saved at
 * once can then never undo each other.
 */
let known: Settings | null = null;
let loading: Promise<Settings> | null = null;
let queue: Promise<unknown> = Promise.resolve();

export function loadSettings(): Promise<Settings> {
  loading ??= api.settings().then(
    (settings) => (known = { ...settings, ...known }),
    (error: unknown) => {
      loading = null;
      throw error;
    },
  );
  return loading.then(() => known!);
}

export function saveSettings(change: { presentation?: Presentation; timeZone?: string | null }): Promise<Settings> {
  const write = queue.then(async () => {
    const base = known ?? (await loadSettings().catch(() => ({ presentation: 'auto' as Presentation })));
    const saved = await api.putSettings({ presentation: change.presentation ?? base.presentation, ...('timeZone' in change ? { timeZone: change.timeZone } : {}) });
    known = saved;
    return saved;
  });
  queue = write.catch(() => {});
  return write;
}

export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

let synced = false;

/** Once per page load for a signed-in person: a server that knows no zone yet is told the browser's. */
export async function syncBrowserTimeZone(): Promise<void> {
  if (synced) return;
  synced = true;
  const zone = browserTimeZone();
  if (!zone) return;
  try {
    const settings = await loadSettings();
    if (!settings.timeZone) await saveSettings({ timeZone: zone });
  } catch {
    synced = false;
  }
}
